import { ParsedUrlQuery } from 'querystring';

import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'next/navigation';

import { callService } from '@api/remote-call';
import {
  convertDateToMomentObject,
  DateType,
  formatMomentDate,
  HOTEL_SEARCH_DATE_FORMAT,
} from '@utils/dateUtil';
import clean from '@utils/cleanObject';
import { useSearchBarContext } from '@contexts/hotel/SearchBar';
import { paramsToObject } from '@components/views/utils';
import useRouterNext from '@hooks/useRouterNext';

export const useGetHotelAvailableDates = ({
  roomId,
  checkInDate,
  checkOutDate,
}: {
  roomId: number;
  runOnMount?: boolean;
  checkInDate?: string;
  checkOutDate?: string;
}) => {
  //======================================
  // Getting the available dates from the backend

  const { data: availableDates, isFetching } = useQuery(
    ['availableHotelDates', roomId],
    () =>
      callService(
        { service: 'HotelsService', method: 'getAvailability', version: 'v3' },
        {
          _in: checkInDate,
          out: checkOutDate,
          roomId,
        }
      ),
    {
      enabled: Boolean(roomId && roomId > 0 && checkInDate && checkOutDate),
    }
  );

  return {
    availableDates,
    isFetching,
  };
};

export const useHotelAvailableDates = () => {
  const params = useParams();
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  const { push } = useRouterNext();
  const [visibleAvailableDates, setVisibleAvailableDates] = useState(false);
  const { startDate: initialStartDate, endDate: initialEndDate } =
    useSearchBarContext();

  const [startDate, setStartDate] = useState<DateType | null>(initialStartDate);
  const [endDate, setEndDate] = useState<DateType | null>(initialEndDate);

  //=================================================
  // Reset the start and end dates when the query changes
  //=================================================
  useEffect(() => {
    setTimeout(() => {
      setStartDate(
        query.in
          ? convertDateToMomentObject(
              query.in as string,
              HOTEL_SEARCH_DATE_FORMAT
            )
          : null
      );
      setEndDate(
        query.out
          ? convertDateToMomentObject(
              query.out as string,
              HOTEL_SEARCH_DATE_FORMAT
            )
          : null
      );
    }, 150); // bypass the ant-design popover time for closing the popover
  }, [query, visibleAvailableDates]);

  //======================================
  // Handle search button
  //======================================
  const handleSearch = () => {
    const { ...nextQuery } = query;
    setVisibleAvailableDates(false);
    push({
      pathname: `/${params?.slug}`,
      query: clean({
        ...nextQuery,
        in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT),
        out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT),
      }),
    });
  };

  return {
    handleSearch,
    setEndDate,
    setStartDate,
    startDate,
    endDate,
    visibleAvailableDates,
    setVisibleAvailableDates,
  };
};
